'use client';

import { FC, useEffect, useMemo, useState } from 'react';

import { ElementProps } from '@ravago/shared/page-data/models/base';
import { RowsAsTabsNode } from '@ravago/shared/page-data/models/layout';
import { Box } from '@ravago/shared/radiance/components/box/components/Box/Box';
import { Button } from '@ravago/shared/radiance/components/button/components/Button/Button';
import { TabProps } from '@ravago/shared/radiance/components/tabs/components/Tab/Tab';
import { Tabs } from '@ravago/shared/radiance/components/tabs/components/Tabs/Tabs';
import { ToggleChip } from '@ravago/shared/radiance/components/toggle-chip/components/ToggleChip/ToggleChip';

function determineTabsComponentAlignment(node: RowsAsTabsNode): Pick<React.ComponentProps<typeof Tabs>, 'alignment' | 'stretch'> {
   switch (node.tabAlignment) {
      case 'RIGHT':
         return { alignment: 'right' };
      case 'CENTER':
         return { alignment: 'center' };
      case 'STRETCH':
         return { stretch: true };
      case 'LEFT':
      default:
         return { alignment: 'left' };
   }
}

function determineChipsAndButtonsBoxAlignment(node: RowsAsTabsNode): Pick<React.ComponentProps<typeof Box>, 'justify' | 'width'> {
   switch (node.tabAlignment) {
      case 'RIGHT':
         return { justify: 'end' };
      case 'CENTER':
         return { justify: 'center' };
      case 'STRETCH':
         return { width: '100%' };
      case 'LEFT':
      default:
         return { justify: 'start' };
   }
}

function determineClassicTabsEdgeSpacing(node: RowsAsTabsNode): Pick<React.ComponentProps<typeof Box>, 'spacing' | 'width'> {
   return {
      spacing: {
         left: node.tabAlignment === 'LEFT' || node.tabAlignment === 'STRETCH' ? undefined : 'xs',
         right: node.tabAlignment === 'RIGHT' || node.tabAlignment === 'STRETCH' ? undefined : 'xs'
      },
      width: node.tabAlignment === 'STRETCH' ? '100%' : undefined
   };
}

function determineActiveChildContainerStyle(node: RowsAsTabsNode): { style?: React.CSSProperties; className?: string } {
   if (node.tabButtonStyle !== 'CLASSIC') {
      return {};
   }

   return {
      style: {
         padding: '24px 0',
         marginTop: '-1px',
         backgroundColor: '#fff',
         border: '1px solid #e7ebee',
         borderRadius: '12px',
         borderTopLeftRadius: node.tabAlignment === 'LEFT' || node.tabAlignment === 'STRETCH' ? '0' : undefined,
         borderTopRightRadius: node.tabAlignment === 'RIGHT' || node.tabAlignment === 'STRETCH' ? '0' : undefined
      }
   };
}

export const RowsAsTabs: FC<ElementProps<RowsAsTabsNode>> = ({ globals, children, node }) => {
   const [activeTabIndex, setActiveTabIndex] = useState<number>(0);

   const tabs = useMemo<Array<TabProps>>(
      () =>
         node.tabLabels.map((tabLabel) => ({
            id: tabLabel.id,
            label: tabLabel.value,
            attributes: { ...(!globals.isConsumer && { 'translation-id': tabLabel.id }) }
         })),
      [node]
   );

   useEffect(() => {
      if (activeTabIndex >= tabs.length) {
         setActiveTabIndex(tabs.length - 1);
      }
   }, [tabs]);

   const handleTabClick = (index: number) => {
      setActiveTabIndex(index);
   };

   const renderTabsAsChips = () =>
      tabs.map((tab, index) => (
         <ToggleChip
            attributes={tab.attributes}
            key={tab.id}
            label={tab.label}
            toggled={activeTabIndex === index}
            onClick={() => handleTabClick(index)}
         />
      ));

   const renderTabsAsButtons = () =>
      tabs.map((tab, index) => (
         <Button
            attributes={tab.attributes}
            key={tab.id}
            variant={activeTabIndex === index ? 'primary' : 'secondary'}
            onClick={() => handleTabClick(index)}
         >
            {tab.label}
         </Button>
      ));

   const renderTabs = () => {
      switch (node.tabButtonStyle) {
         case 'CHIP':
         case 'BUTTON':
            return (
               <Box direction="row" {...determineChipsAndButtonsBoxAlignment(node)} data-testid="rows-as-tabs__non-tab-alignment">
                  <div
                     data-testid="rows-as-tabs__non-tab-stretching"
                     style={{
                        display: 'grid',
                        width: node.tabAlignment === 'STRETCH' ? '100%' : 'fit-content',
                        gap: '8px',
                        gridTemplateColumns: `repeat(${tabs.length}, minmax(0, ${node.uniformTabs ? '1fr' : 'auto'}))`
                     }}
                  >
                     {node.tabButtonStyle === 'CHIP' ? renderTabsAsChips() : renderTabsAsButtons()}
                  </div>
               </Box>
            );
         case 'CLASSIC':
            return (
               <Box {...determineClassicTabsEdgeSpacing(node)}>
                  <Tabs
                     tabs={tabs}
                     tabStyle="classic"
                     uniform={node.uniformTabs}
                     activeTab={activeTabIndex}
                     onTabClick={(index) => handleTabClick(index)}
                     {...determineTabsComponentAlignment(node)}
                  />
               </Box>
            );
         case 'SIMPLE':
         default:
            return (
               <Tabs
                  tabs={tabs}
                  tabStyle="simple"
                  uniform={node.uniformTabs}
                  activeTab={activeTabIndex}
                  onTabClick={(index) => handleTabClick(index)}
                  {...determineTabsComponentAlignment(node)}
               />
            );
      }
   };

   const renderActiveChild = () => {
      const activeChild = children?.[activeTabIndex];
      if (!activeChild) {
         return null;
      }

      return <div {...determineActiveChildContainerStyle(node)}>{activeChild}</div>;
   };

   return (
      <Box direction="column" gap={node.tabButtonStyle === 'CLASSIC' ? 'none' : 'md'}>
         {renderTabs()}
         {renderActiveChild()}
      </Box>
   );
};
